import React, { useCallback, useMemo } from 'react';
import { Form, Input, Modal, Button, Switch, Tree } from 'antd';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { TreeDataNode } from 'antd';
import { ReactComponent as FolderSvg } from '@assets/icons/folder.svg';
import { ReactComponent as PrivateFolderSvg } from '@assets/icons/folder-private.svg';
import { useCampaignsListWithFoldersQuery } from '@hooks/queries/campaign';
import {
  toggleCampaignFolderCreationModal,
  toggleCopyModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import { ReactComponent as ArrowDownSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';

import { useCopyAssetsMutation } from '@hooks/queries/assets';

import './CopyModal.less';

type FormValues = {
  selectedCampaignId: string;
  selectedFolderId: string | null;
  searchText: string;
  copyComments: boolean;
};

type CopyModalAssetsProps = {
  visible: boolean;
  assetIds: string[];
};

export default function CopyModalAssets(props: CopyModalAssetsProps) {
  const { visible, assetIds } = props;
  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();
  const canCreateCampaign = currentWorkspace?.permissions?.createCampaigns;

  const searchText = Form.useWatch('searchText', form) ?? '';
  const selectedCampaignId = Form.useWatch('selectedCampaignId', form) ?? '';
  const selectedFolderId = Form.useWatch('selectedFolderId', form) ?? '';

  const { data: campaignsListQuery, isLoading: isLoadingCampaignsList } =
    useCampaignsListWithFoldersQuery({
      params: {
        workspaceId: currentWorkspace?.id || ''
      }
    });

  const onCreateFolder = useCallback(
    (campaignId: string) => {
      if (!campaignId) return;
      dispatch(
        toggleCampaignFolderCreationModal({
          visible: true,
          entity: 'folder',
          campaignId
        })
      );
    },
    [dispatch]
  );

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        notShowCampaignCreatedModal: true
      })
    );
  }, [dispatch, canCreateCampaign]);

  const campaignsList: TreeDataNode[] = useMemo(() => {
    let _campaignsList = campaignsListQuery ?? [];
    if (searchText) {
      _campaignsList = _campaignsList.filter((campaign) => {
        return campaign.name
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase());
      });
    }
    return _campaignsList.map((item) => ({
      key: item.id,
      title: item.name,
      children: item.folders?.length
        ? [
            ...item.folders.map((folder) => ({
              icon: folder.isPrivate ? (
                <PrivateFolderSvg width={24} height={24} />
              ) : (
                <FolderSvg width={24} height={24} />
              ),
              key: folder.id,
              campaignId: item.id,
              selectable: item.canUploadToRoot,
              title: folder.name
            })),
            {
              key: 'new-folder',
              title: 'New folder',
              campaignId: item.id,
              className: 'new-folder-item',
              icon: <PlusSvg className="b-plus-icon" />
            }
          ]
        : undefined
    }));
  }, [campaignsListQuery, searchText]);

  const { mutateAsync: copyAssets, isPending: isCopyingAssets } =
    useCopyAssetsMutation({});

  const onCloseModal = useCallback(() => {
    dispatch(toggleCopyModal(null));
  }, [dispatch]);

  const onCopyAssets = (data: FormValues) => {
    copyAssets(
      {
        workspaceId: currentWorkspace?.id || '',
        assetIds,
        targetCampaignId: data.selectedCampaignId,
        targetFolderId: data.selectedFolderId ?? undefined,
        copyComments: data.copyComments
      },
      {
        onSuccess: (res) => {
          if (res.data.jobId) {
            localStorage.setItem(
              `copy-link-${res.data.jobId}`,
              `/campaigns/single/${data.selectedCampaignId}/${
                data.selectedFolderId ?? ''
              }`
            );
          }
          onCloseModal();
        }
      }
    );
  };

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossSvg />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onCopyAssets}
        validateTrigger="onChange"
        layout="vertical"
        initialValues={{
          copyComments: true
        }}
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 24 }}
        >
          Copy to
        </h2>
        <Form.Item name="searchText">
          <Input
            placeholder="Search by project"
            prefix={<SearchSvg />}
            size="large"
          />
        </Form.Item>
        <div className="copy-modal__tree-wrapper">
          <Tree
            treeData={campaignsList}
            showIcon={true}
            selectedKeys={
              selectedFolderId ? [selectedFolderId] : [selectedCampaignId]
            }
            onSelect={(_selectedKeys, { node }) => {
              const hasCampaignId = 'campaignId' in node;
              if (node.key === 'new-folder') {
                onCreateFolder(
                  hasCampaignId ? (node.campaignId as string) : ''
                );
                return;
              }
              if (node.selected) {
                form.setFieldsValue({
                  selectedCampaignId: '',
                  selectedFolderId: ''
                });
                return;
              }
              if (hasCampaignId) {
                form.setFieldsValue({
                  selectedCampaignId: node.campaignId as string,
                  selectedFolderId: node.key as string
                });
                return;
              }
              form.setFieldsValue({
                selectedCampaignId: node.key as string,
                selectedFolderId: null
              });
            }}
            switcherIcon={<ArrowDownSvg width={20} height={20} />}
          />
          {canCreateCampaign && (
            <div
              className="copy-modal__action-button"
              onClick={onCreateProject}
            >
              <PlusSvg className="b-plus-icon" />
              New project
            </div>
          )}
        </div>
        <Form.Item name="selectedCampaignId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="selectedFolderId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item
          name="copyComments"
          valuePropName="checked"
          label="Duplicate comments"
          className="duplicate-project-modal__switch"
        >
          <Switch />
        </Form.Item>
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isCopyingAssets}
            disabled={!selectedCampaignId}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            Copy {assetIds?.length ?? ''} items
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
